import React, { useState } from "react";

const uploadsBaseURL = process.env.REACT_APP_API_URL
    ? `${process.env.REACT_APP_API_URL}/uploads`
    : "";

const normalizePhotoUrl = (photoUrl) => {
    if (!photoUrl) return null;
    if (/^https?:\/\//i.test(photoUrl)) return photoUrl;
    if (photoUrl.startsWith("/uploads/") || photoUrl.startsWith("uploads/")) {
        const relativePath = photoUrl.replace(/^\/?uploads\//, "");
        return uploadsBaseURL ? `${uploadsBaseURL}/${relativePath}` : photoUrl;
    }
    return photoUrl;
};

const PreReleaseDetailsModal = ({ preRelease, onClose }) => {
    const [isPhotoOpen, setIsPhotoOpen] = useState(false);
    if (!preRelease) return null;
    const dateObj = new Date(preRelease.createdAt);
    const dateStr = isNaN(dateObj) ? '-' : dateObj.toLocaleDateString();
    const timeStr = isNaN(dateObj) ? '-' : dateObj.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    const products = Array.isArray(preRelease.items) ? preRelease.items : [];
    const photoUrl = normalizePhotoUrl(preRelease.photo_url);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-start justify-center z-50 overflow-y-auto py-6">
            <div className="relative bg-white rounded-lg shadow-lg p-6 min-w-[350px] max-w-[90vw] w-full max-w-md">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
                    aria-label="Close"
                >
                    ✕
                </button>
                <h2 className="text-lg font-bold mb-4 text-center">Pre-Release Details</h2>
                <div className="mb-3 grid grid-cols-2 gap-x-4 gap-y-2">
                    <div><b>Date:</b> {dateStr}</div>
                    <div><b>Time:</b> {timeStr}</div>
                    <div><b>Status:</b> <span className={`inline-block text-xs font-medium rounded-full px-3 py-1 ${preRelease.status === "APPROVED" ? "bg-green-100 text-green-600" : preRelease.status === "REJECTED" ? "bg-red-100 text-red-600" : "bg-yellow-100 text-yellow-800"}`}>{preRelease.status || "PENDING"}</span></div>
                    <div><b>Branch:</b> {preRelease.branch?.name || preRelease.branch_name || preRelease.branch_id}</div>
                    <div><b>Requested by:</b> {preRelease.user?.username || preRelease.user?.email || preRelease.user_id}</div>
                    <div><b>Remarks:</b> {preRelease.remarks || '-'}</div>
                </div>
                <div className="mb-3">
                    <b>Photo:</b>
                    {photoUrl ? (
                        <img
                            src={photoUrl}
                            alt="Pre-release"
                            className="w-32 h-32 object-cover rounded border border-gray-200 mt-2 cursor-zoom-in"
                            onClick={() => setIsPhotoOpen(true)}
                        />
                    ) : (
                        <div className="w-32 h-32 flex items-center justify-center bg-gray-100 rounded border border-gray-200 mt-2">
                            <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path d="M3 7V6a2 2 0 012-2h14a2 2 0 012 2v12a2 2 0 01-2 2H5a2 2 0 01-2-2v-1" />
                                <circle cx="8.5" cy="8.5" r="1.5" />
                                <path d="M21 15l-5-5L5 21" />
                            </svg>
                        </div>
                    )}
                </div>
                <div className="mb-2 mt-4">
                    <div className="font-semibold mb-2 sticky top-0 bg-white">Products:</div>
                    {products.length > 0 ? (
                        <div className="border rounded overflow-hidden">
                            <div className="max-h-60 overflow-y-auto">
                                <table className="w-full text-xs">
                                    <thead className="sticky top-0 bg-gray-100 z-10">
                                        <tr>
                                            <th className="py-1 px-2 text-left">Product Name</th>
                                            <th className="py-1 px-2 text-center">Qty</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {products.map((item, idx) => (
                                            <tr key={idx} className="border-t">
                                                <td className="py-1 px-2">{item.product?.name || '-'}</td>
                                                <td className="py-1 px-2 text-center">{item.qty || 0}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="bg-gray-50 border-t text-xs font-semibold">
                                <table className="w-full">
                                    <tbody>
                                        <tr>
                                            <td className="py-1">Total</td>
                                            <td className="py-1 px-5 text-right">{products.reduce((sum, item) => sum + (item.qty || 0), 0)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <div className="text-gray-500 italic mt-1">No products</div>
                    )}
                </div>
                <button className="mt-6 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 w-full" onClick={onClose}>Close</button>
            </div>
            {isPhotoOpen && photoUrl && (
                <div
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]"
                    onClick={() => setIsPhotoOpen(false)}
                >
                    <div className="relative max-w-[95vw] max-h-[95vh]" onClick={(e) => e.stopPropagation()}>
                        <button
                            type="button"
                            className="absolute -top-3 -right-3 bg-white text-gray-800 rounded-full shadow px-2 py-1 text-xs"
                            onClick={() => setIsPhotoOpen(false)}
                            aria-label="Close photo"
                        >
                            ✕
                        </button>
                        <img
                            src={photoUrl}
                            alt="Pre-release full"
                            className="max-w-[95vw] max-h-[95vh] object-contain rounded"
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default PreReleaseDetailsModal;
